import { useMemo, useState } from 'react'
import type { ProductoCatalogo, BonoCatalogo } from './catalogo'
import { bonosDelCatalogo, packsDelCatalogo, sueltosDelCatalogo } from './catalogo'
import type { FilaTabla } from './tabla'
import { DIAS, bonoDelHabitual } from './tabla'
import { colorDeDia } from './coloresDia'
import type { DiaRuta } from '../../hooks/useFiltrosAgendamiento'
import { IconoCerrar } from '../../componentes/iconos'

/**
 * Panel lateral de reparto (rediseño B.3 · R9).
 *
 * Al pulsar un cliente en la tabla se abre a la derecha este panel, donde se
 * arma su reparto completo: el bono (seleccionable ±2 del mismo formato), packs
 * y botellones sueltos como extras, y el día de reparto de ese cliente. Antes la
 * tabla solo dejaba «Elegir bono» apretado en la fila; la spec pide una sección
 * de productos que se despliega al pulsar el cliente, con TODOS los productos
 * para los clientes comunes (bonos + packs + sueltos), no solo bonos.
 *
 * El día por cliente (B.3) sustituye a la fecha única de la tanda: cada cliente
 * puede repartirse un día distinto. Si el cliente no tiene ese día en sus rutas,
 * se avisa (la spec lo permite: «ese día no hay ruta en su dirección»).
 */

/** Un extra elegido (pack o suelto) con su cantidad. */
export interface ExtraElegido {
  productoId: number
  nombre: string
  precio: number
  cantidad: number
}

interface Props {
  fila: FilaTabla
  catalogo: ProductoCatalogo[]
  catalogoPendiente: boolean
  /** Bono elegido para este cliente (o el habitual si no se ha tocado). */
  bonoElegido: BonoCatalogo | null
  /** Extras (packs/sueltos) ya elegidos para este cliente. */
  extras: ExtraElegido[]
  /** Día de reparto elegido para este cliente (r1..r5), o null. */
  dia: DiaRuta | null
  onElegirBono: (contactId: string, productoId: string) => void
  onCambiarExtras: (contactId: string, extras: ExtraElegido[]) => void
  onElegirDia: (contactId: string, dia: DiaRuta | null) => void
  onCerrar: () => void
}

const eur = (n: number) => `${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

/** Precio a MOSTRAR: con IVA (como pide la spec), con fallback al base. */
function precioMostrado(p: { precio: number; precio_con_iva?: number }): number {
  return p.precio_con_iva ?? p.precio
}

export function PanelReparto({
  fila,
  catalogo,
  catalogoPendiente,
  bonoElegido,
  extras,
  dia,
  onElegirBono,
  onCambiarExtras,
  onElegirDia,
  onCerrar,
}: Props) {
  const contactId = fila.cliente.contact_id
  const [buscar, setBuscar] = useState('')

  const todosLosBonos = useMemo(() => bonosDelCatalogo(catalogo), [catalogo])
  const packs = useMemo(() => packsDelCatalogo(catalogo), [catalogo])
  const sueltos = useMemo(() => sueltosDelCatalogo(catalogo), [catalogo])

  // El habitual marca el formato: el seleccionable ±2 es del mismo litraje.
  const habitual = useMemo(
    () => bonoElegido ?? bonoDelHabitual(todosLosBonos, fila.pedido_habitual),
    [bonoElegido, todosLosBonos, fila.pedido_habitual],
  )

  // Seleccionable ±2 del mismo formato (R8). Sin habitual, se ofrecen todos los
  // bonos para que el administrador pueda elegir formato manualmente.
  const bonosOfrecibles = useMemo(() => {
    if (!habitual) return todosLosBonos
    const centro = habitual.unidades
    return todosLosBonos.filter(
      (b) => b.litraje === habitual.litraje && b.unidades >= centro - 2 && b.unidades <= centro + 2,
    )
  }, [habitual, todosLosBonos])

  const rutasCliente = new Set(fila.cliente.rutas ?? [])
  const idExtra = (id: number) => extras.find((e) => e.productoId === id)

  const alternarExtra = (p: ProductoCatalogo) => {
    const ya = idExtra(p.id)
    if (ya) {
      onCambiarExtras(contactId, extras.filter((e) => e.productoId !== p.id))
    } else {
      onCambiarExtras(contactId, [
        ...extras,
        { productoId: p.id, nombre: p.nombre, precio: p.precio, cantidad: 1 },
      ])
    }
  }

  const cambiarCantidad = (id: number, delta: number) => {
    onCambiarExtras(
      contactId,
      extras.map((e) =>
        e.productoId === id ? { ...e, cantidad: Math.max(1, e.cantidad + delta) } : e,
      ),
    )
  }

  const filtrar = (lista: ProductoCatalogo[]) => {
    const q = buscar.trim().toLowerCase()
    if (!q) return lista
    return lista.filter((p) => p.nombre.toLowerCase().includes(q))
  }

  return (
    <aside
      aria-label={`Reparto de ${fila.cliente.nombre}`}
      className="flex flex-col gap-4 rounded-xl border border-borde bg-superficie p-4"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-tinta">{fila.cliente.nombre}</h2>
          <p className="mt-0.5 text-xs text-tinta-apagada">
            {fila.cliente.code || 'sin NIF'} · habitual{' '}
            {habitual ? `Bono ${habitual.unidades} (${habitual.litraje}L)` : 'sin bono'}
          </p>
        </div>
        <button
          type="button"
          aria-label="Cerrar el panel"
          onClick={onCerrar}
          className="rounded-lg p-1 text-tinta-apagada transition-colors hover:bg-elevado hover:text-tinta"
        >
          <IconoCerrar className="size-5" />
        </button>
      </div>

      {catalogoPendiente ? (
        <p className="text-sm text-tinta-apagada">Cargando el catálogo…</p>
      ) : (
        <>
          {/* Bono ±2 */}
          <Grupo titulo={`Bono${habitual ? ` · ±2 (${habitual.litraje}L)` : ''}`}>
            <div className="flex flex-wrap gap-2">
              {bonosOfrecibles.map((bono) => {
                const sel = habitual?.id === bono.id
                return (
                  <button
                    key={bono.id}
                    type="button"
                    onClick={() => onElegirBono(contactId, String(bono.id))}
                    className={tarjeta(sel)}
                  >
                    <span className="text-sm font-semibold">Bono {bono.unidades}</span>
                    <span className={sel ? 'text-xs text-acento-tinta' : 'text-xs text-tinta-apagada'}>
                      {eur(precioMostrado(bono))}
                    </span>
                    {bonoDelHabitual(todosLosBonos, fila.pedido_habitual)?.id === bono.id ? (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-positivo-tinta">
                        habitual
                      </span>
                    ) : null}
                  </button>
                )
              })}
              {bonoElegido ? (
                <button
                  type="button"
                  onClick={() => onElegirBono(contactId, '')}
                  className="rounded-lg border border-borde-fuerte px-3 py-2 text-xs font-medium text-tinta-2 transition-colors hover:bg-elevado"
                >
                  Quitar bono
                </button>
              ) : null}
            </div>
          </Grupo>

          {/* Buscador de extras */}
          <input
            type="search"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Buscar pack o botellón…"
            className="w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
          />

          {/* Packs */}
          {filtrar(packs).length > 0 ? (
            <Grupo titulo="Packs (¿algo más?)">
              <ListaExtras
                productos={filtrar(packs)}
                idExtra={idExtra}
                onAlternar={alternarExtra}
                onCantidad={cambiarCantidad}
                sufijo="/ud"
              />
            </Grupo>
          ) : null}

          {/* Botellones sueltos */}
          {filtrar(sueltos).length > 0 ? (
            <Grupo titulo="Botellones sueltos">
              <ListaExtras
                productos={filtrar(sueltos)}
                idExtra={idExtra}
                onAlternar={alternarExtra}
                onCantidad={cambiarCantidad}
                sufijo=""
              />
            </Grupo>
          ) : null}

          {/* Día de reparto por cliente */}
          <Grupo titulo="Día de reparto (este cliente)">
            <div className="flex flex-wrap gap-2">
              {DIAS.map((d) => {
                const sel = dia === d.clave
                const tiene = rutasCliente.has(d.clave)
                const color = colorDeDia(d.clave)
                return (
                  <button
                    key={d.clave}
                    type="button"
                    onClick={() => onElegirDia(contactId, sel ? null : d.clave)}
                    className={[
                      'flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                      sel ? 'border-acento bg-acento-suave text-tinta' : 'border-borde-fuerte text-tinta-2 hover:bg-elevado',
                    ].join(' ')}
                    title={tiene ? '' : 'Este cliente no tiene ruta ese día'}
                  >
                    <span className={`inline-flex size-4 items-center justify-center rounded-full text-[9px] ${color.clase}`}>
                      {d.clave.slice(1)}
                    </span>
                    {d.corta}
                    {!tiene ? <span className="text-[10px] text-atencion-tinta">sin ruta</span> : null}
                  </button>
                )
              })}
            </div>
            {dia && !rutasCliente.has(dia) ? (
              <p className="mt-2 text-xs text-atencion-tinta">
                Ese día no hay ruta en la dirección de este cliente.
              </p>
            ) : null}
          </Grupo>
        </>
      )}
    </aside>
  )
}

function Grupo({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">{titulo}</span>
        <span className="h-px flex-1 bg-borde" />
      </div>
      {children}
    </div>
  )
}

function ListaExtras({
  productos,
  idExtra,
  onAlternar,
  onCantidad,
  sufijo,
}: {
  productos: ProductoCatalogo[]
  idExtra: (id: number) => ExtraElegido | undefined
  onAlternar: (p: ProductoCatalogo) => void
  onCantidad: (id: number, delta: number) => void
  sufijo: string
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {productos.map((p) => {
        const elegido = idExtra(p.id)
        return (
          <div key={p.id} className="flex items-center gap-1">
            <button type="button" onClick={() => onAlternar(p)} className={tarjeta(Boolean(elegido))}>
              <span className="text-sm font-semibold">{p.nombre}</span>
              <span className={elegido ? 'text-xs text-acento-tinta' : 'text-xs text-tinta-apagada'}>
                {eur(precioMostrado(p))}
                {sufijo}
              </span>
            </button>
            {elegido ? (
              <span className="inline-flex items-center overflow-hidden rounded-lg border border-borde-fuerte">
                <button
                  type="button"
                  aria-label={`Quitar una unidad de ${p.nombre}`}
                  onClick={() => onCantidad(p.id, -1)}
                  className="bg-elevado px-2 py-1.5 text-sm text-tinta-2 hover:text-tinta"
                >
                  −
                </button>
                <span className="min-w-8 text-center text-sm font-semibold text-tinta">
                  {elegido.cantidad}
                </span>
                <button
                  type="button"
                  aria-label={`Añadir una unidad de ${p.nombre}`}
                  onClick={() => onCantidad(p.id, 1)}
                  className="bg-elevado px-2 py-1.5 text-sm text-tinta-2 hover:text-tinta"
                >
                  +
                </button>
              </span>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/** Tarjeta seleccionable de producto (bono/pack/suelto). */
function tarjeta(sel: boolean): string {
  return [
    'flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-left transition-colors',
    sel
      ? 'border-acento bg-acento-suave text-tinta'
      : 'border-borde-fuerte text-tinta-2 hover:border-acento hover:bg-elevado',
  ].join(' ')
}
